/**
 * Die Weltkarte.
 *
 * Ein Marker je Tauchplatz, nicht je Tauchgang — sonst läge auf einem Hausriff
 * mit 40 Tauchgängen ein Stapel aus 40 Nadeln. Die Grösse zeigt die Anzahl, die
 * Farbe die durchschnittliche Tiefe: dieselbe Skala wie in Liste, Profil und
 * Histogramm. Ein Blick auf die Karte zeigt damit, wo flach und wo tief getaucht
 * wurde.
 *
 * Gruppiert wird über ein eigenes Gitter statt über eine Cluster-Bibliothek:
 * 194 Plätze rechtfertigen keine weitere Abhängigkeit.
 *
 * **Ohne Netz bleiben die Kacheln leer, die Marker nicht.** Der Kartengrund
 * kommt von OpenStreetMap; die Tauchplätze liegen lokal. Das ist so vorgesehen
 * (Projektplan, Risikoliste) — die Umkreissuche funktioniert auch dann.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { gruppiere, hatKoordinaten, plaetzeMitTauchgaengen, umkreis } from '../geo.js';
import { koordinaten, tiefe } from '../format.js';
import { grundwahl, grundwahlLeiste, haltGroesseNach } from '../kartengrund.js';
import { leseKoordinaten, passt, suchworte } from '../verortung.js';
import { zuHash } from '../suche.js';
import { tiefenfarbe } from '../tiefenfarbe.js';

const RADIEN = [10, 25, 50, 100, 250];
const ZOOM_TREFFER = 12;

function radius(anzahl) {
  return Math.min(22, 6 + Math.sqrt(anzahl) * 2.4);
}

function platzInhalt(platz) {
  const zeitraum = platz.erster === platz.letzter
    ? (platz.erster ?? '').slice(0, 4)
    : `${(platz.erster ?? '').slice(0, 4)}–${(platz.letzter ?? '').slice(0, 4)}`;
  return `<strong>${platz.name}</strong><br>`
    + `${platz.anzahl} ${platz.anzahl === 1 ? 'Tauchgang' : 'Tauchgänge'} · ${zeitraum}<br>`
    + `tiefster ${tiefe(platz.maxTiefe)}${platz.country ? ` · ${platz.country}` : ''}<br>`
    + `<a href="${zuHash({ platz: platz.name })}">Tauchgänge zeigen</a>`;
}

export function ansichtKarte(alleTauchgaenge, allePlaetze) {
  const wurzel = document.createElement('section');
  wurzel.className = 'karte-seite';

  const plaetze = plaetzeMitTauchgaengen(alleTauchgaenge, allePlaetze);
  const ohneKoordinaten = allePlaetze.filter((p) => !hatKoordinaten(p)).length;

  wurzel.innerHTML = `
    <header class="karte-kopf">
      <h1>Weltkarte</h1>
      <p class="karte-stand">${plaetze.length} Tauchplätze mit Koordinaten${
  ohneKoordinaten ? ` · ${ohneKoordinaten} ohne, meist Tauchgänge vor 2018` : ''}</p>
    </header>
    <div class="karte-suchzeile">
      <input type="search" class="kartensuche" placeholder="Platz, Land, Gewässer …"
        aria-label="Tauchplätze durchsuchen">
      <input type="text" class="koordinatenfeld kartenkoordinaten"
        placeholder="46.9757, 8.3403 · 46°58'32&quot;N 8°20'25&quot;E · Google-Maps-Link"
        aria-label="Koordinaten einfügen">
      <button type="button" class="knopf karte-springen">Hier suchen</button>
    </div>
    <p class="karte-meldung" role="status" aria-live="polite"></p>
    <div class="karte-leiste">
      <span class="karte-hinweis">Karte anklicken für die Umkreissuche</span>
      ${grundwahlLeiste()}
      <label>Umkreis
        <select class="karte-radius">${RADIEN.map((km) =>
    `<option value="${km}"${km === 50 ? ' selected' : ''}>${km} km</option>`).join('')}</select>
      </label>
      <button type="button" class="karte-standort">Mein Standort</button>
    </div>
    <div class="karte" id="karte"></div>
    <div class="umkreis-ergebnis" hidden></div>`;

  if (plaetze.length === 0) {
    wurzel.querySelector('#karte').innerHTML =
      '<p class="leer">Kein Tauchplatz hat Koordinaten. Nach <code>make geocode</code> '
      + 'und einem neuen Export sind sie da.</p>';
    return wurzel;
  }

  const ergebnisbereich = wurzel.querySelector('.umkreis-ergebnis');
  const radiusfeld = wurzel.querySelector('.karte-radius');
  const stand = wurzel.querySelector('.karte-stand');
  const grund = grundwahl(wurzel.querySelector('.grundwahl'));
  let karte = null;
  let loeseGroesse = null;
  let ebene = null;
  let probe = null;
  let sichtbar = plaetze;

  function zeichneMarker() {
    if (!karte) return;
    ebene?.remove();
    ebene = L.layerGroup().addTo(karte);
    for (const gruppe of gruppiere(sichtbar, karte.getZoom())) {
      const einzeln = gruppe.plaetze.length === 1;
      const marker = L.circleMarker([gruppe.lat, gruppe.lon], {
        radius: radius(gruppe.anzahl),
        color: tiefenfarbe(gruppe.schnittTiefe ?? 0),
        weight: einzeln ? 1.5 : 2,
        fillColor: tiefenfarbe(gruppe.schnittTiefe ?? 0),
        fillOpacity: einzeln ? 0.45 : 0.3,
      }).addTo(ebene);
      if (einzeln) {
        marker.bindPopup(platzInhalt(gruppe.plaetze[0]));
      } else {
        marker.bindTooltip(`${gruppe.plaetze.length} Plätze · ${gruppe.anzahl} Tauchgänge`);
        marker.on('click', () => karte.setView([gruppe.lat, gruppe.lon], karte.getZoom() + 3));
      }
    }
  }

  /**
   * Die Volltextsuche über die Plätze.
   *
   * Sie blendet aus, statt hervorzuheben: Wer "Malediven" sucht, will die
   * Malediven sehen und nicht die 190 anderen Marker daneben. Der Ausschnitt
   * folgt den Treffern, sonst müsste man sie nach dem Tippen erst suchen.
   */
  function sucheAus(text) {
    const worte = suchworte(text);
    sichtbar = worte.length === 0 ? plaetze : plaetze.filter((p) => passt(p, worte));
    stand.textContent = worte.length === 0
      ? `${plaetze.length} Tauchplätze mit Koordinaten${ohneKoordinaten
        ? ` · ${ohneKoordinaten} ohne, meist Tauchgänge vor 2018` : ''}`
      : `${sichtbar.length} von ${plaetze.length} Tauchplätzen`;
    zeichneMarker();
    if (worte.length === 0 || sichtbar.length === 0 || !karte) return;
    karte.fitBounds(L.latLngBounds(sichtbar.map((p) => [p.lat, p.lon])),
      { padding: [40, 40], maxZoom: ZOOM_TREFFER });
  }

  function springeZu(punkt) {
    karte?.setView([punkt.lat, punkt.lon], Math.max(karte.getZoom(), 9));
    zeigeUmkreis(punkt);
  }

  function zeigeUmkreis(punkt) {
    const km = Number(radiusfeld.value);
    const treffer = umkreis(plaetze, punkt, km);
    probe?.remove();
    probe = L.layerGroup([
      L.circle([punkt.lat, punkt.lon], {
        radius: km * 1000, color: '#f4b66a', weight: 1, fillOpacity: 0.05,
      }),
      L.circleMarker([punkt.lat, punkt.lon], {
        radius: 4, color: '#f4b66a', fillColor: '#f4b66a', fillOpacity: 1,
      }),
    ]).addTo(karte);

    const tauchgaenge = treffer.reduce((summe, t) => summe + t.platz.anzahl, 0);
    ergebnisbereich.hidden = false;
    ergebnisbereich.innerHTML = treffer.length === 0
      ? `<p class="umkreis-leer">Im Umkreis von ${km} km bist du noch nicht getaucht.</p>`
      : `<h2>${treffer.length} ${treffer.length === 1 ? 'Platz' : 'Plätze'} im Umkreis von `
        + `${km} km · ${tauchgaenge} Tauchgänge</h2>`
        + `<ol class="umkreis-liste">${treffer.slice(0, 25).map((t) => `<li>
            <a href="${zuHash({ platz: t.platz.name })}">
              <span class="umkreis-name">${t.platz.name}</span>
              <span class="umkreis-km">${t.km < 1 ? '< 1' : Math.round(t.km)} km</span>
              <span class="umkreis-anzahl">${t.platz.anzahl}×</span>
            </a></li>`).join('')}</ol>`;
  }

  // Leaflet braucht ein Element, das schon im Dokument haengt und eine Hoehe hat.
  requestAnimationFrame(() => {
    const behaelter = wurzel.querySelector('#karte');
    karte = L.map(behaelter, {
      worldCopyJump: true,
      // Ohne Leaflets Vorspann: Der traegt eine Flagge und Eigenwerbung. Die
      // Herkunft der Kacheln bleibt — sie ist die Bedingung, unter der man sie
      // benutzen darf, und steht klein und blass am Rand.
      attributionControl: false,
    });
    L.control.attribution({ prefix: false }).addTo(karte);
    const ausschnitt = L.latLngBounds(plaetze.map((p) => [p.lat, p.lon]));
    loeseGroesse = haltGroesseNach(karte, behaelter, ausschnitt);
    grund.anKarte(karte);
    karte.fitBounds(ausschnitt, { padding: [30, 30] });
    zeichneMarker();
    karte.on('zoomend', zeichneMarker);
    karte.on('click', (ereignis) => zeigeUmkreis({
      lat: ereignis.latlng.lat, lon: ereignis.latlng.lng,
    }));
    if (!navigator.onLine) {
      wurzel.querySelector('.karte-hinweis').textContent =
        'Ohne Netz bleibt der Kartengrund leer — die Tauchplätze liegen lokal.';
    }
  });

  wurzel.querySelector('.kartensuche').addEventListener('input', (ereignis) => {
    sucheAus(ereignis.target.value);
  });

  const koordinatenfeld = wurzel.querySelector('.kartenkoordinaten');
  const ausFeld = () => {
    const punkt = leseKoordinaten(koordinatenfeld.value);
    const meldung = wurzel.querySelector('.karte-meldung');
    // Eigene Zeile, nicht der Hinweis daneben: Eine Fehlermeldung in der
    // Werkzeugleiste schiebt die Knöpfe um, sobald sie länger wird.
    meldung.classList.toggle('fehler', !punkt);
    if (!punkt) {
      meldung.textContent = 'Daraus lässt sich keine Position lesen — zwei Zahlen, '
        + 'Grad/Minuten/Sekunden oder ein Google-Maps-Link.';
      return;
    }
    meldung.textContent = `Umkreis um ${koordinaten(punkt.lat, punkt.lon)}`;
    springeZu(punkt);
  };
  wurzel.querySelector('.karte-springen').addEventListener('click', ausFeld);
  koordinatenfeld.addEventListener('keydown', (ereignis) => {
    if (ereignis.key === 'Enter') { ereignis.preventDefault(); ausFeld(); }
  });

  radiusfeld.addEventListener('change', () => {
    const mitte = probe?.getLayers?.()[0]?.getLatLng?.();
    if (mitte) zeigeUmkreis({ lat: mitte.lat, lon: mitte.lng });
  });

  wurzel.querySelector('.karte-standort').addEventListener('click', (ereignis) => {
    const knopf = ereignis.currentTarget;
    if (!navigator.geolocation) {
      knopf.textContent = 'Standort nicht verfügbar';
      return;
    }
    knopf.textContent = 'Suche Standort …';
    navigator.geolocation.getCurrentPosition(
      (ort) => {
        knopf.textContent = 'Mein Standort';
        springeZu({ lat: ort.coords.latitude, lon: ort.coords.longitude });
      },
      () => { knopf.textContent = 'Standort abgelehnt'; },
      { timeout: 10000 },
    );
  });

  wurzel.aufraeumen = () => {
    loeseGroesse?.();
    karte?.remove();
    karte = null;
  };
  return wurzel;
}
